<template>
  <nav
    v-if="segments.length"
    class="nav-breadcrumb"
    aria-label="Dónde estás"
  >
    <template v-for="(segment, index) in segments">
      <feather-icon
        v-if="index"
        :key="`sep-${segment.key}`"
        icon="ChevronRightIcon"
        size="14"
        class="nav-breadcrumb__separator"
      />
      <router-link
        v-if="segment.to"
        :key="segment.key"
        :to="segment.to"
        class="nav-breadcrumb__item nav-breadcrumb__item--link"
      >
        {{ segment.text }}
      </router-link>
      <span
        v-else
        :key="segment.key"
        class="nav-breadcrumb__item nav-breadcrumb__item--current"
      >
        {{ segment.text }}
      </span>
    </template>
  </nav>
</template>

<script>
import { mapGetters } from 'vuex'

// El ultimo tramo de la ruta, por nombre de pantalla. Lo que no esta aqui no inventa
// etiqueta: se queda con la obra y la cotizacion, que ya dicen bastante.
const SECTION_LABELS = {
  quotes: 'Cotizaciones',
  newQuotes: 'Nueva cotización',
  approvedQuotes: 'Mis compras',
  editQuote: 'Editar cotización',
  quoteInvoices: 'Facturas',
  purchaseOrder: 'Orden de compra',
  purchaseOrders: 'Órdenes de compra',
  purchaseOrderInvoices: 'Facturas de la orden',
  remissions: 'Remisiones',
  bankAccountData: 'Datos bancarios',
  groupedExpenses: 'Explosión de insumos',
  addGroupedExpenses: 'Nueva explosión de insumos',
  budget: 'Presupuesto',
  expenses: 'Egresos por producto',
  allExpenses: 'Egresos',
  newExpense: 'Nuevo egreso',
  expensesByGroup: 'Egresos del grupo',
  incomes: 'Ingresos',
  newIncome: 'Nuevo ingreso',
  editIncome: 'Editar ingreso',
  reports: 'Dashboard',
  incomesVsExpenses: 'Ingresos vs egresos',
  expensesReport: 'Reporte de egresos',
  editProject: 'Editar obra',

  // Pantallas de la cuenta: no cuelgan de ninguna obra, asi que estas son el unico tramo.
  projects: 'Mis proyectos',
  newProjects: 'Nueva obra',
  editCustomer: 'Editar cuenta',
  members: 'Usuarios',
  'new-member': 'Nuevo usuario',
  allMyQuotes: 'Todas mis cotizaciones',
  allMyExpenses: 'Todos mis egresos',
  accountsReceivable: 'Estado de cuenta',
  WalletForCustomer: 'Monedero',
  referrals: 'Referidos',
  billing: 'Facturación',
  'new-billing': 'Alta de facturación',
  membership: 'Mi membresía',
  membershipPlansSettings: 'Membresías',
  membershipRequests: 'Solicitudes de membresía',
}

// Pantallas que son la cotizacion misma: ahi el folio ya es el ultimo tramo y repetirlo
// como seccion sobraria.
const QUOTE_OWN_VIEWS = ['checkout', 'clientQuote', 'prequote', 'clientQuoteMetadata']

// Los mismos estados de pago con los que "Mis compras" arma su lista. Con esto el
// encabezado dice de que lista viene la cotizacion sin depender de por donde se llego,
// que cambiaria al recargar.
const PURCHASED_PAYMENT_STATUSES = ['awaiting_payment_approval', 'payment_approved', 'credit_approved']

export default {
  computed: {
    ...mapGetters('projects', ['project']),
    ...mapGetters('quotes', ['quote']),

    routeQuoteId() {
      return this.$route.params.quoteId || null
    },

    // El store guarda la ultima cotizacion vista, asi que solo se usa cuando es la de esta
    // pantalla; si no, el encabezado mostraria un folio que no es.
    currentQuote() {
      if (!this.routeQuoteId) return null
      if (this.quote?.id !== this.routeQuoteId) return null

      return this.quote
    },

    // Una cotizacion pagada, aprobada a credito o esperando validacion ya es una compra:
    // es el mismo corte que usa la pantalla de Mis compras.
    quoteIsPurchase() {
      return PURCHASED_PAYMENT_STATUSES.includes(this.currentQuote?.payment_status)
    },

    // La obra solo encabeza a las pantallas que cuelgan de ella. En las de la cuenta el
    // store puede traer todavia la obra de la visita anterior, y ponerla ahi seria mentir.
    isProjectScoped() {
      return Boolean(this.$route.params.projectId) || Boolean(this.currentQuote)
    },

    segments() {
      const segments = []

      if (this.isProjectScoped && this.project?.id) {
        segments.push({
          key: 'project',
          text: this.project.name,
          to: this.project.customer_id
            ? { name: 'projects', params: { customerId: this.project.customer_id } }
            : { name: 'quotes', params: { projectId: this.project.id } },
        })
      }

      if (this.currentQuote?.folio) {
        if (this.project?.id) {
          segments.push({
            key: 'origin',
            text: this.quoteIsPurchase ? 'Mis compras' : 'Cotizaciones',
            to: {
              name: this.quoteIsPurchase ? 'approvedQuotes' : 'quotes',
              params: { projectId: this.project.id },
            },
          })
        }

        segments.push({
          key: 'quote',
          text: `Folio #${this.currentQuote.folio}`,
          to: QUOTE_OWN_VIEWS.includes(this.$route.name)
            ? null
            : { name: 'checkout', params: { quoteId: this.routeQuoteId } },
        })
      }

      const section = SECTION_LABELS[this.$route.name]
      if (section) segments.push({ key: 'section', text: section, to: null })

      // El ultimo tramo nunca es enlace: es donde ya estas.
      if (segments.length) segments[segments.length - 1].to = null

      return segments
    },
  },
}
</script>

<style lang="scss" scoped>
.nav-breadcrumb {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  min-width: 0;
  flex-wrap: nowrap;
  overflow: hidden;
}

.nav-breadcrumb__item {
  font-size: 1.05rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.65);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 11rem;
}

.nav-breadcrumb__item--link:hover {
  color: #ffffff;
  text-decoration: underline;
}

.nav-breadcrumb__item--current {
  color: #ffffff;
  font-weight: 700;
  max-width: 18rem;
}

.nav-breadcrumb__separator {
  flex-shrink: 0;
  color: rgba(255, 255, 255, 0.4);
}

@media (max-width: 767px) {
  .nav-breadcrumb__item {
    font-size: 0.95rem;
    max-width: 9rem;
  }

  .nav-breadcrumb__item:not(.nav-breadcrumb__item--current),
  .nav-breadcrumb__separator {
    display: none;
  }
}
</style>
